import React from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, MessageCircle, FileText, Zap, ShieldCheck, ArrowRight, CheckCircle2,
  Bot, Rocket, Cpu,
} from 'lucide-react';
import './ChatGptDisputePromptPage.css';

const CHECKOUT_URL = 'https://www.commas.com/checkout/gVv2kUiBJ5UWpgigG';

const STEPS = [
  {
    icon: Cpu,
    title: 'Open ChatGPT',
    desc: 'Head to chat.openai.com or open the ChatGPT app. Works on the free tier too.',
  },
  {
    icon: FileText,
    title: 'Paste the Prompt',
    desc: 'Drop in our engineered dispute prompt (delivered instantly after checkout).',
  },
  {
    icon: MessageCircle,
    title: 'Feed It Your Item',
    desc: 'Enter the negative item you want removed — collection, late payment, charge-off, inquiry.',
  },
  {
    icon: Rocket,
    title: 'Send the Letter',
    desc: 'Copy the generated dispute letter, print it, mail it to the bureau or furnisher. Done.',
  },
];

const INCLUDES = [
  'The full engineered ChatGPT dispute prompt',
  'Works for Equifax, Experian & TransUnion disputes',
  'Handles collections, charge-offs, late payments & inquiries',
  'CROA/FCRA-compliant letter structure built in',
  'Method-of-verification demand language included',
  'Debt-validation letter variant included',
  'Reusable — generate unlimited letters, unlimited items',
  'Instant delivery — access the moment you check out',
];

const FAQS = [
  {
    q: 'Do I need a paid ChatGPT account?',
    a: 'No. The prompt works on the free tier of ChatGPT. Plus/Pro just makes it a little faster, but is not required.',
  },
  {
    q: 'Do I need any credit knowledge to use this?',
    a: 'None. The prompt does the heavy lifting — you literally paste it, answer a couple of questions, and it writes a compliant dispute letter for you.',
  },
  {
    q: 'How many letters can I generate?',
    a: 'Unlimited. Use it for every negative item on your report, for yourself, or for family. One-time purchase.',
  },
  {
    q: 'Is this legal / compliant?',
    a: 'Yes. The prompt is engineered around CROA and FCRA compliant language — factual disputes, method-of-verification demands, and debt-validation requests. It does not generate CPN routing, ID-swap tactics, or anything shady.',
  },
];

const ChatGptDisputePromptPage = () => {
  return (
    <div className="dark-page chatgpt-dispute-page">
      {/* Hero */}
      <section className="chatgpt-hero">
        <div className="chatgpt-hero-inner">
          <div className="chatgpt-badge" data-testid="chatgpt-badge">
            <Sparkles size={14} /> AI DISPUTE PROMPT · CHATGPT
          </div>
          <h1 className="display-huge">
            Turn <span className="chatgpt-hero-accent">ChatGPT</span><br />
            Into Your <span className="chatgpt-hero-accent">Personal Dispute Weapon</span>.
          </h1>
          <p className="body-large chatgpt-subtitle">
            One engineered prompt. Free ChatGPT. Unlimited dispute letters —
            written in seconds, aimed at every collection, late payment, charge-off,
            and inquiry sitting on your credit report.
          </p>

          <div className="chatgpt-hero-cta">
            <a
              href={CHECKOUT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary chatgpt-cta-btn"
              data-testid="chatgpt-cta-top"
            >
              Get The Prompt Now <ArrowRight size={20} />
            </a>
            <span className="chatgpt-hero-note">
              Instant access · One-time purchase · Unlimited letters
            </span>
          </div>

          {/* Phone mockup: ChatGPT conversation preview */}
          <div className="chatgpt-phone-mockup" data-testid="chatgpt-phone-mockup" aria-hidden="true">
            <div className="chatgpt-phone-frame">
              <div className="chatgpt-phone-notch" />
              <div className="chatgpt-phone-screen">
                <div className="chatgpt-app-header">
                  <div className="chatgpt-app-header-dot" />
                  <span>ChatGPT</span>
                  <span className="chatgpt-app-header-tag">Free</span>
                </div>
                <div className="chatgpt-chat-body">
                  <div className="chatgpt-msg chatgpt-msg-user">
                    <span className="chatgpt-msg-label">You</span>
                    <p>Generate a dispute letter for a Portfolio Recovery collection on my Experian report — $842, opened 2023.</p>
                  </div>
                  <div className="chatgpt-msg chatgpt-msg-bot">
                    <span className="chatgpt-msg-label">ChatGPT</span>
                    <p><strong>Drafting compliant dispute letter…</strong></p>
                    <p className="chatgpt-msg-preview">
                      "Per the FCRA §611 and CROA §1679b, I formally dispute the following item reporting on my Experian file: <em>Portfolio Recovery Associates · $842 · Opened 2023.</em> I demand method-of-verification and full debt validation from the original creditor within 30 days…"
                    </p>
                    <div className="chatgpt-msg-actions">
                      <span>📋 Copy</span>
                      <span>✉️ Mail today</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="chatgpt-phone-caption">
              Actual output. Compliant. In seconds.
            </div>
          </div>

          <div className="chatgpt-hero-stats">
            <div className="chatgpt-hero-stat">
              <div className="chatgpt-hero-stat-num">30s</div>
              <div className="chatgpt-hero-stat-label">Per Letter</div>
            </div>
            <div className="chatgpt-hero-stat">
              <div className="chatgpt-hero-stat-num">∞</div>
              <div className="chatgpt-hero-stat-label">Letters Generated</div>
            </div>
            <div className="chatgpt-hero-stat">
              <div className="chatgpt-hero-stat-num">$0</div>
              <div className="chatgpt-hero-stat-label">ChatGPT Cost</div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="chatgpt-steps">
        <h2 className="display-medium chatgpt-section-title">How It Works</h2>
        <p className="chatgpt-section-sub">
          Four taps between you and a compliant dispute letter you can mail today.
        </p>
        <div className="chatgpt-steps-grid">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.title} className="chatgpt-step-card" data-testid={`chatgpt-step-${i}`}>
                <div className="chatgpt-step-num">{String(i + 1).padStart(2, '0')}</div>
                <div className="chatgpt-step-icon"><Icon size={22} /></div>
                <h3 className="chatgpt-step-title">{s.title}</h3>
                <p className="chatgpt-step-desc">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* What's included */}
      <section className="chatgpt-includes">
        <div className="chatgpt-includes-card">
          <div className="chatgpt-includes-head">
            <Bot size={22} />
            <h3 className="chatgpt-includes-title">What You're Getting</h3>
          </div>
          <ul className="chatgpt-includes-list">
            {INCLUDES.map((line, i) => (
              <li key={i} data-testid={`chatgpt-include-${i}`}>
                <CheckCircle2 size={16} /> {line}
              </li>
            ))}
          </ul>
          <div className="chatgpt-includes-foot">
            <a
              href={CHECKOUT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary chatgpt-cta-btn chatgpt-cta-btn-lg"
              data-testid="chatgpt-cta-middle"
            >
              Buy The Prompt <ArrowRight size={18} />
            </a>
            <p className="chatgpt-foot-note">
              Secure checkout via Commas. Instant delivery. No monthly fees, ever.
            </p>
          </div>
        </div>
      </section>

      {/* Why it works */}
      <section className="chatgpt-why">
        <div className="chatgpt-why-grid">
          <div className="chatgpt-why-card">
            <Zap size={22} />
            <h4>Speed Kills Delinquency</h4>
            <p>Old-school dispute companies take 30-90 days per round. ChatGPT writes the same-quality letter in 30 seconds — you mail today, dispute clock starts today.</p>
          </div>
          <div className="chatgpt-why-card">
            <ShieldCheck size={22} />
            <h4>Compliance Built In</h4>
            <p>The prompt is engineered around CROA / FCRA language, method-of-verification demands, and debt-validation escalation — no risky tactics, no CPN routing.</p>
          </div>
          <div className="chatgpt-why-card">
            <FileText size={22} />
            <h4>Unlimited Letters</h4>
            <p>One purchase. Use it for every negative item you have, then keep using it for your family, friends, or clients. There is no per-letter cost.</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="chatgpt-faq">
        <h2 className="display-medium chatgpt-section-title">Common Questions</h2>
        <div className="chatgpt-faq-list">
          {FAQS.map((f, i) => (
            <details className="chatgpt-faq-item" key={i} data-testid={`chatgpt-faq-${i}`}>
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="chatgpt-final-cta">
        <div className="chatgpt-final-cta-inner">
          <h3>Ready to Draft Your First Dispute Letter Today?</h3>
          <p>Stop paying $99+/month to a dispute mill. Pay once. Fix credit forever.</p>
          <a
            href={CHECKOUT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary chatgpt-cta-btn chatgpt-cta-btn-lg"
            data-testid="chatgpt-cta-bottom"
          >
            Get The Prompt Now <ArrowRight size={18} />
          </a>
          <div className="chatgpt-final-cta-links">
            <Link to="/credit-repair">Prefer full done-for-you credit repair? →</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ChatGptDisputePromptPage;
